import { Search, X } from "lucide-mui";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import type { Ref } from "react";

interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minWidth?: number;
  /** Leading search icon. */
  icon?: boolean;
  /** Trailing clear button while there is text. */
  clearable?: boolean;
  autoFocus?: boolean;
  fullWidth?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  /** A key that focuses the field, shown at its end while it's empty ("/"). */
  shortcut?: string;
}

/** Small free-text search input. */
export default function SearchField({
  value,
  onChange,
  placeholder,
  minWidth,
  icon = true,
  clearable = false,
  autoFocus,
  fullWidth,
  inputRef,
  shortcut,
}: SearchFieldProps) {
  let endAdornment = null;
  if (clearable && value) {
    endAdornment = (
      <InputAdornment position="end">
        <IconButton size="small" aria-label="Clear search" onClick={() => onChange("")}>
          <X fontSize="small" />
        </IconButton>
      </InputAdornment>
    );
  } else if (shortcut && !value) {
    endAdornment = (
      <InputAdornment position="end">
        <Box
          component="kbd"
          sx={{
            fontFamily: "inherit",
            fontSize: "0.6875rem",
            lineHeight: 1,
            color: "text.secondary",
            border: 1,
            borderColor: "divider",
            borderRadius: 0.75,
            px: 0.75,
            py: 0.375,
          }}
        >
          {shortcut}
        </Box>
      </InputAdornment>
    );
  }
  return (
    <TextField
      size="small"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoFocus={autoFocus}
      fullWidth={fullWidth}
      inputRef={inputRef}
      slotProps={{
        input: {
          startAdornment: icon ? (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ) : undefined,
          endAdornment,
        },
      }}
      sx={{ minWidth }}
    />
  );
}
